import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Heart, Menu, X, User, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const isBackoffice = location.pathname.startsWith('/backoffice');

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <Heart className="w-6 h-6 text-primary fill-primary group-hover:scale-110 transition-transform" />
            <span className="font-serif text-2xl font-semibold text-foreground">
              Mi Boda Perfecta
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/vendors"
              className="text-muted-foreground hover:text-primary transition-colors font-medium"
            >
              Proveedores
            </Link>
            <Link
              to="/dashboard"
              className="text-muted-foreground hover:text-primary transition-colors font-medium"
            >
              Invitaciones
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {isBackoffice ? (
              <Link to="/">
                <Button variant="outline">Ver Marketplace</Button>
              </Link>
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2">
                    <User className="w-4 h-4" />
                    Mi cuenta
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="w-full cursor-pointer">
                      Mis invitaciones
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2">
                    <User className="w-4 h-4" />
                    Iniciar sesión
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/auth" className="w-full cursor-pointer">
                      Soy novio
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/backoffice" className="w-full cursor-pointer text-muted-foreground">
                      Soy proveedor
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-4">
              <Link
                to="/vendors"
                className="text-muted-foreground hover:text-primary transition-colors font-medium"
                onClick={() => setIsOpen(false)}
              >
                Proveedores
              </Link>
              {user ? (
                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                  <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start gap-2 pl-4">
                      <User className="w-4 h-4" />
                      Mis invitaciones
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 pl-4"
                    onClick={handleSignOut}
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar sesión
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                  <div className="px-4 py-2">
                    <span className="text-sm font-semibold text-muted-foreground">Iniciar sesión</span>
                  </div>
                  <Link to="/auth" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start gap-2 pl-8">
                      <User className="w-4 h-4" />
                      Soy novio
                    </Button>
                  </Link>
                  <Link to="/backoffice" onClick={() => setIsOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start gap-2 pl-8">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Soy proveedor</span>
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
